import type { PageGuide } from './guideTypes'
import { guideRegistry } from '../../data/guide'
import { menuItems, type MenuItem } from '../../data/menuItems'
import { buildDetailedGuide } from '../../data/guide/guideFactory'

function flattenMenu(items: MenuItem[], acc: { path: string; label: MenuItem['label'] }[] = []) {
  for (const item of items) {
    if (item.path) acc.push({ path: item.path, label: item.label })
    if (item.children) flattenMenu(item.children, acc)
  }
  return acc
}

const flatMenu = flattenMenu(menuItems)

/** Match exact, prefix*, or /:param segments */
export function pathMatches(pattern: string, pathname: string): boolean {
  if (pattern.endsWith('*')) {
    const prefix = pattern.slice(0, -1)
    return pathname === prefix.replace(/\/$/, '') || pathname.startsWith(prefix)
  }

  if (pattern.includes(':')) {
    const patternParts = pattern.split('/').filter(Boolean)
    const pathParts = pathname.split('/').filter(Boolean)
    if (patternParts.length !== pathParts.length) return false
    return patternParts.every((p, i) => p.startsWith(':') || p === pathParts[i])
  }

  return pathname === pattern
}

function scoreMatch(pattern: string, pathname: string): number {
  if (!pathMatches(pattern, pathname)) return -1
  if (pattern.endsWith('*')) return pattern.length - 1
  if (pattern.includes(':')) return pattern.length + 500
  // Exact path — prefer longest
  if (pathname === pattern) return pattern.length + 2000
  return pattern.length
}

function findInRegistry(pathname: string): PageGuide | null {
  let best: PageGuide | null = null
  let bestScore = -1

  for (const guide of guideRegistry) {
    const patterns = Array.isArray(guide.match) ? guide.match : [guide.match]
    for (const p of patterns) {
      const score = scoreMatch(p, pathname)
      if (score > bestScore) {
        best = guide
        bestScore = score
      }
    }
  }
  return best
}

function findMenuLabel(pathname: string): MenuItem['label'] | null {
  const exact = flatMenu.find((m) => m.path === pathname)
  if (exact) return exact.label

  let best: (typeof flatMenu)[0] | null = null
  for (const m of flatMenu) {
    if (pathname === m.path || pathname.startsWith(m.path + '/')) {
      if (!best || m.path.length > best.path.length) best = m
    }
  }
  return best?.label ?? null
}

function humanizePath(pathname: string): MenuItem['label'] {
  const last = pathname.split('/').filter(Boolean).pop() ?? 'page'
  const nice = last.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
  return {
    ar: nice,
    en: nice,
    fr: nice,
  }
}

/** Detailed fallback when no registry entry matches */
export function buildFallbackGuide(pathname: string): PageGuide {
  const label = findMenuLabel(pathname) ?? humanizePath(pathname)
  return buildDetailedGuide({ path: pathname, label })
}

export function resolveGuide(pathname: string): PageGuide {
  return findInRegistry(pathname) ?? buildFallbackGuide(pathname)
}
